/* eslint-disable react/prop-types */
import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea,
} from 'recharts';

/**
 * Daily breath sessions against alerts for one user (042).
 *
 * Dual axis on purpose: the two quantities differ by an order of magnitude, and a
 * shared axis would flatten alerts into the baseline.
 *
 * Both series are lines (changed 2026-07-21 by request; previously breath was drawn
 * as bars). Two lines on independent axes CAN mislead — a reader naturally compares
 * heights, and here heights mean nothing across series. Mitigated with a dashed
 * stroke for breath, axis-side named in the legend, and both axes labelled.
 *
 * The shaded band is the pause window. Note that `ReferenceArea` renders NOTHING at
 * all if `yAxisId` is missing or if x1/x2 do not match actual x-axis values, and a
 * missing band looks like "no data" rather than a bug — hence the explicit yAxisId
 * and the test that asserts it.
 *
 * First use of ComposedChart / ReferenceArea / dual-axis in this codebase.
 */

const BREATH_COLOR = '#f26b45';      // coral — house palette
const ALERT_COLOR = '#1f4d78';       // navy
const WINDOW_COLOR = '#94a3b8';      // slate — neutral pause-window outline
const IMPROVEMENT_COLOR = '#2e9b87'; // teal — days below the baseline rate

/**
 * Contiguous runs of pause-window days whose alert count sits BELOW the user's
 * baseline rate — i.e. the parts of the pause period where alerts actually fell.
 *
 * Deliberately strict (`<`, not `<=`): a day exactly at the baseline rate did not
 * decrease, and shading it would overstate the finding.
 *
 * Returns [] when there is no usable baseline, so a `no_baseline` user gets no
 * improvement shading at all — there is nothing to have improved against.
 *
 * Exported for testing; the run-grouping is the part worth pinning down.
 */
export const improvementBands = (series, baselineRate) => {
  if (!Array.isArray(series) || !(Number(baselineRate) > 0)) return [];

  const rate = Number(baselineRate);
  const bands = [];
  let start = null;
  let last = null;

  series.forEach((row) => {
    // Baseline-region days can be low too, but they are the comparison — never
    // the improvement. Only in-window days can be shaded.
    const improving = row.in_pause && Number(row.alerts) < rate;
    if (improving) {
      if (start === null) start = row.date;
      last = row.date;
    } else if (start !== null) {
      bands.push({ x1: start, x2: last });
      start = null;
      last = null;
    }
  });

  if (start !== null) bands.push({ x1: start, x2: last });

  return bands;
};

const TooltipContent = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="pai-tooltip">
      <div className="pai-tooltip__date">{label}</div>
      <div className="pai-tooltip__row">
        <span style={{ color: BREATH_COLOR }}>■</span> Breath sessions: <b>{row.breath_sessions ?? 0}</b>
      </div>
      <div className="pai-tooltip__row">
        <span style={{ color: ALERT_COLOR }}>■</span> Alerts: <b>{row.alerts ?? 0}</b>
      </div>
      <div className="pai-tooltip__region">
        {row.in_pause ? 'During the pause period' : 'Before the pause period (baseline)'}
      </div>
    </div>
  );
};

const PauseAlertImpactChart = ({ series = [], span = null, truncated = false, baselineRate = 0 }) => {
  const enough = Array.isArray(series) && series.length >= 2;
  const bands = improvementBands(series, baselineRate);

  return (
    <div className="au-panel" data-testid="pai-chart">
      <div className="au-panel__head">
        <h4 className="au-panel__title">Breath sessions vs alerts, per day</h4>
        <span className="au-spacer" />
        <div className="au-legend" data-testid="pai-legend">
          <span><i className="pai-swatch-dashed" style={{ borderColor: BREATH_COLOR }} />Breath sessions (left axis)</span>
          <span><i style={{ background: ALERT_COLOR }} />Alerts (right axis)</span>
          <span><i className="pai-swatch-outline" />Pause period</span>
          <span><i style={{ background: IMPROVEMENT_COLOR, opacity: 0.4 }} />Alerts below baseline</span>
        </div>
      </div>

      {truncated && (
        <p className="au-chart-note pai-truncated" data-testid="pai-chart-truncated">
          This user’s history is longer than the chart can show. The earliest days have
          been trimmed, so the baseline shown here is shorter than the 30 days used to
          compute the figures above.
        </p>
      )}

      <p className="au-chart-note pai-band-note" data-testid="pai-band-note">
        The dashed outline marks the pause period. Shading inside it marks the days
        whose alert count fell <b>below</b> this user’s baseline rate
        {Number(baselineRate) > 0 ? <> of <b>{Number(baselineRate).toFixed(3)}</b>/day</> : null}
        {bands.length === 0 && Number(baselineRate) > 0
          ? ' — there were none.'
          : bands.length === 0 ? ' — no baseline to compare against.' : '.'}
      </p>

      {enough ? (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f1" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} minTickGap={24} />
            <YAxis
              yAxisId="left"
              width={44}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              label={{ value: 'Breath', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#94a3b8' }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={44}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              label={{ value: 'Alerts', angle: 90, position: 'insideRight', fontSize: 11, fill: '#94a3b8' }}
            />
            <Tooltip content={<TooltipContent />} />

            {/* Bands paint first so they sit behind the data. yAxisId is REQUIRED
                on every one — without it Recharts drops the element silently. */}

            {/* Neutral outline of the whole pause window. Always drawn, so the period
                stays locatable even for a user whose alerts did not fall. Unfilled:
                it marks where to look, it does not claim a result. */}
            {span?.pause_start && span?.pause_end && (
              <ReferenceArea
                yAxisId="left"
                x1={span.pause_start}
                x2={span.pause_end}
                fill="none"
                fillOpacity={0}
                stroke={WINDOW_COLOR}
                strokeOpacity={0.5}
                strokeDasharray="4 3"
              />
            )}

            {/* The parts that actually improved: in-window days below the baseline
                rate. Several runs are normal — daily counts are noisy. */}
            {bands.map((b) => (
              <ReferenceArea
                key={`${b.x1}-${b.x2}`}
                data-kind="improvement"
                yAxisId="left"
                x1={b.x1}
                x2={b.x2}
                fill={IMPROVEMENT_COLOR}
                fillOpacity={0.18}
                stroke="none"
              />
            ))}

            {/* Two lines on two axes. Because both are lines, the dash pattern —
                not just the colour — distinguishes them, and the axis labels say
                which side each belongs to. Their heights are NOT comparable: the
                scales are independent. */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="breath_sessions"
              name="Breath sessions"
              stroke={BREATH_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="alerts"
              name="Alerts"
              stroke={ALERT_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="au-chart-empty" data-testid="pai-chart-empty">
          Not enough data to plot this user’s window (need at least 2 days).
        </div>
      )}
    </div>
  );
};

export default PauseAlertImpactChart;
